import { useEffect, useRef, useState } from 'react';
import {
  ExpandedState,
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel
} from '@tanstack/react-table';
import { useAppSelector } from 'src/hooks';
import TableComponent, { makeColumnsSticky } from './table';

export default function CollapsibleTable({
  data,
  columns,
  tableState = {},
  extClassName = '',
  setHeightExplicitly = true,
  showFooterLabel = true,
  externalColumnData = [],
  visibleColumns = {},
  activeHeader = ''
}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const tableRef = useRef<HTMLTableElement>();
  const { cogsDialogVisible } = useAppSelector((state) => state.plannerV2);
  
  // Validate and process columns before passing to useReactTable
  const validatedColumns = columns.map(column => {
    // Ensure each column has a valid id
    if (!column.id && column.accessorKey) {
      column.id = column.accessorKey;
    } else if (!column.id && column.parent && column.parent.parent_field) {
      column.id = column.parent.parent_field;
    } else if (!column.id) {
      // Generate a fallback id if none is available
      column.id = `col-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    // Ensure each column has an accessorFn if no accessorKey is provided
    if (!column.accessorKey && !column.accessorFn) {
      column.accessorFn = (row) => {
        // Try to access data using parent_field if available
        if (column.parent && column.parent.parent_field) {
          return row[column.parent.parent_field];
        }
        return undefined;
      };
    }
    
    return column;
  });
  
  const table = useReactTable({
    data: Array.isArray(data) ? data : [], // Ensure data is an array
    columns: validatedColumns,
    state: {
      expanded,
      ...tableState
    },
    onExpandedChange: (newExpanded) => {
      setExpanded(newExpanded);
    },
    getSubRows: (row: any) => {
      return row.subRows || row.children || []},
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel()
  });

  useEffect(() => {
    if (tableRef.current) {
      // cogsVisible for handling column spacing on cogs table
      makeColumnsSticky(tableRef.current, cogsDialogVisible);
    }
  }, [tableState, expanded, cogsDialogVisible]);

  return (
    <TableComponent
      setHeightExplicitly={setHeightExplicitly}
      table={table}
      extClassName={extClassName}
      tableRef={tableRef}
      showFooterLabel={showFooterLabel}
      externalColumnData={externalColumnData}
      visibleColumns={visibleColumns}
      activeHeader={activeHeader}
    />
  );
}